"use client";
import { useTranslations } from "next-intl";
import * as dc from "@/components/layout/list/defaultType";
import { scaleLinear } from "d3-scale";
import { Container, DataList, Flex, Skeleton } from "@radix-ui/themes";
import { Datum, LegendProps } from "@nivo/legends";
import type { DataElement, DataInputCommonParams, DataInputParams, InputTypeData, RadarProps } from "./inputType";
import { Form, Skeleton as AntSkeleton, Radio, Select, FormInstance, Button } from "antd";
import { Debug } from "@/util/debug";
import { checkNoItem, GetDataInputElement, runFormFunction } from "./inputFunction";
import { TransitionType } from "@/components/layout/layoutType";
import { GridLabelComponent, MouseEventHandler } from "@nivo/radar";
import DataInput, { GetDataLabel } from "../data/dataInput";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { getDataKeyLabel, getDataKeyList, getDataKeyName, getDataKeyValue } from "@/util/datautil";
import { getStringArr, getStringLabel, getStringRecord } from "@/util/arrayutil";
import { PropertyAccessor } from "@nivo/core";
import { cn } from "@/lib/utils";
import { nivoTheme } from "../list/defaultType";

import { ResponsiveRadar } from "@nivo/radar";
import Icon from "@/components/common/icon/icon";
import { exportChartAsPNG } from "@/util/fileutil";
import { AlertContext } from "@/components/context/alertContext";
// import dynamic from "next/dynamic";
// const ResponsiveRadar = dynamic(
//   () =>
//     import("@nivo/radar")
//       .then((mod) => mod.ResponsiveRadar)
//       .catch(() => {
//         const DummyComponent = () => <></>;
//         return DummyComponent;
//       }),
//   { ssr: false },
// );

type IndexInfo = {
  name: string | undefined;
  keys: string[] | undefined;
  keyLabels: Record<string, string> | undefined;
  legendData: Datum[];
  legends: LegendProps[] | undefined;
  fields: string[][] | undefined;
  fieldLabel: Record<string, string>[];
  indexBy: PropertyAccessor<Record<string, any>, string>;
  multi: boolean | undefined;
  convert: boolean;
};
const isTrainsitionFieldLabel = (params: DataInputParams, field: string) => {
  return (
    (params.radarProp?.fieldInputProp && params.radarProp?.fieldInputProp[field]
      ? params.radarProp?.fieldInputProp[field].labelTransition
      : undefined) ??
    params.radarProp?.commonInputProp?.labelTransition ??
    params.labelTransition ??
    true
  );
};
// const getFormChartData = (t: TransitionType, indexInfo: IndexInfo, params: DataInputParams, value: any) => {
//   const e = typeof value == "number" ? value : 0;
//   if (indexInfo.fields) {
//     const result = indexInfo.fields?.map((field) => {
//       const temp: Record<string, unknown> = {};
//       const indexBy = typeof indexInfo.indexBy == "string" ? indexInfo.indexBy : indexInfo.indexBy.toString();
//       temp[indexBy] = isTrainsitionFieldLabel(params, field) ? t(indexInfo.fieldLabel[field]) : indexInfo.fieldLabel[field];
//       indexInfo.keys?.forEach((key, index) => {
//         temp[indexInfo.keyLabel ? indexInfo.keyLabel[key] ?? "" : ""] =
//           params.form.getFieldValue(indexInfo.multi ? field + "_" + key : field) ?? getDefaultValue(params, field);
//       });
//       return temp;
//     });
//     return result;
//   } else {
//     //Debug("return fields isnull");
//     return [{}];
//   }
// };
const getDefaultValue = (params: DataInputParams, field: string) => {
  return params.inputMode == "view" && (params.noItem ?? true)
    ? 0
    : params.radarProp?.fieldInputProp &&
        params.radarProp?.fieldInputProp[field] &&
        params.radarProp?.fieldInputProp[field].defaultValue !== undefined
      ? params.radarProp?.fieldInputProp[field].defaultValue
      : params.radarProp?.commonInputProp?.defaultValue !== undefined
        ? params.radarProp?.commonInputProp?.defaultValue
        : (params.defaultValue ?? 0);
};

export const dataConverter = (t: TransitionType, indexInfo: IndexInfo, params: DataInputParams): Record<string, unknown>[] => {
  //Debug(indexInfo);
  if (indexInfo.convert) {
    if (params.radarProp?.data) {
      //Debug("return data");
      return params.radarProp?.data;
    } else if (params.values === undefined) {
      //Debug("return values undefiend");
      return params.values;
    } else {
      if (indexInfo.fields) {
        const inputMode = params.inputMode === "view" && !params.noItem;
        const valueArr = inputMode ? params.values : params.form ? params.form.getFieldsValue() : {};
        const result: Record<string, any>[] = [{}];
        indexInfo.fields.forEach((fields, index) => {
          const temp: Record<string, unknown> = {};
          fields.forEach((field, field_index) => {
            if (result.length < field_index + 1) {
              result.push({});
            }
            const temp = result[field_index];
            const indexBy = typeof indexInfo.indexBy == "string" ? indexInfo.indexBy : indexInfo.indexBy.toString();
            const fieldLabel = indexInfo.fieldLabel[index][field];
            temp[indexBy] = fieldLabel && isTrainsitionFieldLabel(params, field) ? t(fieldLabel) : fieldLabel;
            temp["key"] = field;
            indexInfo.keys?.forEach((key) => {
              temp[key] = valueArr[field] ?? 0;
            });
          });
        });
        return result;
      } else {
        //Debug("return fields isnull");
        return [{}];
      }
    }
  } else {
    if (params.radarProp?.data) {
      return params.radarProp?.data;
    } else if (params.values instanceof Array) {
      return params.values;
    } else if (params.values === undefined) {
      return [{}];
    } else {
      return [params.values];
    }
  }
};

// const test = () => {
//   <ResponsiveRadar keys={["chardonay", "carmenere", "syrah"]} data={[{}]} indexBy="taste" />;
// };

export function DataRadarItem(
  params: RadarProps & {
    name?: string | undefined;
    noItem: boolean;
    keyLabels: Record<string, string> | undefined;
    form?: FormInstance;
    formFunction?: Record<string, Record<string, Function>> | undefined;
  },
) {
  const svgRef = useRef<SVGSVGElement>(null);
  const margin = {
    top: params.margin?.top ?? 80,
    right: params.margin?.right ?? 80,
    bottom: params.margin?.bottom ?? 70,
    left: params.margin?.left ?? 80,
  };

  const maxValue = useMemo(() => {
    if (params.maxValue === "auto") {
      const data = (params.data ?? [{}]) as Record<string, unknown>[];
      return Math.max(
        ...data?.flatMap((d) => {
          return params.keys ? params.keys.map((key) => (d[key] as number) ?? 0) : 0;
        }),
      );
    } else {
      return params.maxValue ?? 0;
    }
  }, [params.data, params.keys, params.maxValue]);

  //Debug(params.data);
  // const temp_le:LegendProps = {
  //   anchor: "bottom-right",
  //   direction: "column",
  //   translateX: -150,
  //   translateY: -140,
  //   itemWidth: 80,
  //   itemHeight: 20,
  //   itemTextColor: "#999",
  //   symbolSize: 12,
  //   symbolShape: "circle",
  // }
  // temp_le.
  const handleClick: MouseEventHandler<Record<string, unknown>, SVGPathElement> = (point, event) => {
    const indexBy = "key";
    const field = "field";
    const keys = params.keys;
    const data = (params.data ?? [{}]) as Record<string, unknown>[];

    if (!svgRef.current || !keys) return;

    const rect = svgRef.current.getBoundingClientRect();
    const center = {
      x: (rect.width - margin.left - margin.right) / 2 + margin.left,
      y: (rect.height - margin.top - margin.bottom) / 2 + margin.top,
    };
    const centerX = center.x - (event.clientX - rect.left);
    const centerY = center.y - (event.clientY - rect.top);

    const chartSize = Math.min(rect.width - margin.left - margin.right, rect.height - margin.top - margin.bottom);
    const radius = chartSize / 2;

    // radius scale
    const radiusScale = scaleLinear().domain([0, maxValue]).range([0, radius]);

    // 클릭 좌표 (div 기준)
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const dx = x - center.x;
    const dy = y - center.y;

    const r = Math.sqrt(dx * dx + dy * dy);
    const value = Math.round((r * maxValue) / radius);
    const angle = Math.atan2(dy, dx) + (-(params.rotation ?? 0) * Math.PI) / 180;
    // Debug("좌표:", { key: point.key, value, r, angle });
    const key = point.key as string;
    if (point.key as string) {
      params.form?.setFieldValue(key, value);
      runFormFunction(params.formFunction, key, "inputSync");
    }
    runFormFunction(params.formFunction, params.name, "changeData");
    // Debug(point, margin, rect, center, centerX, centerY, radius);

    // 각 축 각도
    // const indices = data.map((d) => d[indexBy]);
    // const anglePerIndex = (2 * Math.PI) / indices.length;

    // let nearest: { index: string; key: string; value: Number } | null = null;
    // let minDist = Infinity;

    // data.forEach((d, i) => {
    //   const theta = i * anglePerIndex - Math.PI / 2;
    //   keys.forEach((key) => {
    //     const value = d[key];
    //     if (!(value instanceof Number)) return;

    //     const rr = radiusScale(value);
    //     const px = rr * Math.cos(theta);
    //     const py = rr * Math.sin(theta);

    //     const dist = Math.sqrt((dx - px) ** 2 + (dy - py) ** 2);

    //     if (dist < minDist) {
    //       minDist = dist;
    //       nearest = { index: d[indexBy] as string, key, value };
    //     }
    //   });
    // });
    // Debug("가장 가까운 데이터:", nearest);
  };
  if (!ResponsiveRadar) {
  }
  return (
    <ResponsiveRadar
      ref={svgRef}
      theme={nivoTheme}
      keys={["chardonay", "carmenere", "syrah"]}
      indexBy="taste"
      //valueFormat=">-.1f"
      margin={margin}
      borderColor={{ from: "color" }}
      gridLabelOffset={36}
      dotSize={10}
      dotColor={{ theme: "background" }}
      dotBorderWidth={2}
      //colors={{ scheme: "nivo" }}
      blendMode="multiply"
      motionConfig="gentle"
      onClick={(e: any, a: any) => {
        if (!params.noItem) {
          Debug(e, a, params.data);
          handleClick(e, a);
        }
      }}
      sliceTooltip={(data) => {
        if (!params.noItem) {
          return <></>;
        }
        //Debug(data);
        return (
          <div style={dc.chartCoverDivstyle}>
            <div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  <tr>
                    {/* <td></td> */}
                    <td style={{ padding: "3px 5px" }} colSpan={2}>
                      <strong>{typeof data.index === "string" ? data.index : ""}</strong>
                    </td>
                  </tr>
                  {data.data.map((i, index) => {
                    return (
                      <tr key={"sliceTooltip_" + index}>
                        <td style={{ padding: "3px 5px" }}>
                          <span
                            style={{ display: "block", width: "1rem", height: "1rem", background: i.color, marginRight: "0.5rem" }}
                          ></span>
                        </td>
                        <td style={{ padding: "3px 5px" }}>{params.keyLabels ? params.keyLabels[i.id] : i.id}</td>
                        <td style={{ padding: "3px 5px" }}>
                          <span style={{ fontWeight: "bold" }}>{i.formattedValue}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      }}
      curve="linearClosed" //    | 'basisClosed'| 'cardinalClosed'| 'catmullRomClosed'| 'linearClosed'
      gridShape="linear" //circular, linear
      // legends={[
      //   {
      //     anchor: "bottom-right",
      //     direction: "column",
      //     translateX: -150,
      //     translateY: -140,
      //     itemWidth: 80,
      //     itemHeight: 20,
      //     itemTextColor: "#999",
      //     symbolSize: 12,
      //     symbolShape: "circle",
      //     effects: [
      //       {
      //         on: "hover",
      //         style: {
      //           itemTextColor: "#000",
      //         },
      //       },
      //     ],
      //   },
      // ]}
      {...params}
      data={params.data ?? [{}]}
    />
  );
}
const getRadarInputType = (params: DataInputParams): InputTypeData => {
  switch (params.inputType) {
    case "radarRate":
      return "rateInput";
    case "radarSlider":
      return "sliderInput";
    case "radarInput":
    default:
      return "inputNumber";
  }
};
export default function DataRadar(params: DataInputParams) {
  const t = useTranslations(params.transitionGroup ?? "contents");
  const prev = params.prev ? GetDataInputElement(params.prev, false, params.values, t) : undefined;
  const next = params.next ? GetDataInputElement(params.next, true, params.values, t) : undefined;
  const [changeLabel, setChangeLabel] = useState<boolean>(true);
  const [animate, setAnimate] = useState<boolean>(true);

  const DataLabel: DataElement =
    typeof params.label === "string" || params.label === undefined
      ? { element: params.label ?? (params.labelNamePrev ?? "") + params.name }
      : params.label;

  const indexInfo: IndexInfo = useMemo(() => {
    if (changeLabel) {
      setChangeLabel(false);
    }
    const name = getDataKeyName(params.indexKey);
    const keys = params.indexKey ? getStringArr(params.indexKey) : undefined; //params.radarProp?.keys ?? getDataKeyList(params) ?? [];
    const keyLabels: Record<string, string> | undefined = keys ? {} : undefined;
    if (keys && keyLabels) {
      keys.forEach((key, index) => {
        const field = params.indexLabelKey ? params.indexLabelKey[index] : key;
        if (field) {
          if (params.form && params.form.getFieldInstance(field)) {
            keyLabels[key] = params.form.getFieldValue(field);
          } else {
            keyLabels[key] = params.values[field];
          }
        }
      });
    }
    // const dataLabel = getDataKeyLabel(params);
    // const radarKeys = params.radarProp?.keys ?? dataLabel;
    // const keysArr = keys.map((key) => {
    //   return keyLabels ? keyLabels[key] : key;
    // });
    const fields = params.fieldList ? params.fieldList.map((item) => getStringArr(item)) : undefined;
    const fieldLabel = params.fieldList ? params.fieldList.map((item) => getStringRecord(item)) : [{}];
    const multi = keys && keys.length > 1;
    const legendData: Datum[] = keyLabels
      ? Object.keys(keyLabels).map((key) => {
          return { id: key, label: keyLabels[key] };
        })
      : [];
    const convert = params.radarProp?.data === undefined || (params.radarProp?.dataConvert ?? true);
    const indexBy = params.radarProp?.indexBy ?? "field";
    const legends = params.radarProp && params.radarProp?.legends ? [...params.radarProp.legends] : undefined;
    if (legends && legends.length > 0 && legendData.length > 0) {
      legends.forEach((legend, index) => (legends[index] = { ...legend, data: legendData }));
    }

    const result = { name, keys, keyLabels, legendData, legends, fields, fieldLabel, indexBy, multi, convert };
    // Debug("indexInfo", result);
    return result;
  }, [params, changeLabel]);
  // Debug(indexInfo);
  const [data, setData] = useState<Record<string, unknown>[]>(dataConverter(t, indexInfo, params));

  if (params.formFunction && params.name) {
    if (!params.formFunction[params.name]) {
      params.formFunction[params.name] = {};
    }
    params.formFunction[params.name]["changeData"] = () => {
      const changeData = dataConverter(t, indexInfo, params);
      // Debug(indexInfo, changeData);
      setData(changeData);
    };
    params.formFunction[params.name]["changeLabel"] = () => {
      // setAnimate(false);
      setChangeLabel(true);
      // setTimeout(() => {
      //   setAnimate(true);
      // }, dc.setTimeoutDelay);
    };
  }

  if (params.resetFunction && params.name) {
    params.resetFunction[params.name] = () => {
      setData(dataConverter(t, indexInfo, params));
    };
  }
  useEffect(() => {
    const changeData = dataConverter(t, indexInfo, params);
    //const changeData = getFormChartData(t, indexInfo, params, {});
    // Debug(data);
    // Debug(changeData);
    // setAnimate(false);
    setData(changeData);
    // setTimeout(() => {
    //   setAnimate(true);
    // }, dc.setTimeoutDelay);
  }, [indexInfo, params, t]);
  const gridLabel: GridLabelComponent = (e) => {
    //const label = indexInfo.fieldLabel[e.id] ?? e.id;
    return (
      <g transform={`translate(${e.x},${e.y})`} dominantBaseline="central" textAnchor="middle">
        <text fill="var(--gray-a11)">{e.id}</text>
      </g>
    );
  };

  const radarProp: RadarProps = {
    maxValue: typeof params.maxValue === "number" ? params.maxValue : undefined,
    gridLabel: gridLabel,
    keys: indexInfo.keys,
    indexBy: indexInfo.indexBy,
    animate: animate,
    ...params.radarProp,
    legends: indexInfo.legends,
  };
  const noItem = params.inputMode === "view"; //checkNoItem(params);

  const ta = useTranslations("alert");
  const layoutRef = useRef<HTMLDivElement>(null);
  const alertContext = useContext(AlertContext);
  const GetButtons = () => {
    return (
      <Flex className={cn(" w-full text-right relative right-2 z-10 chart-tool screenAnimation h-0 flex-row-reverse")}>
        {params.imageDownload ? (
          <Button
            className={cn("ml-2")}
            icon={<Icon iconType="" iconName="DownloadSimple" weight="bold" />}
            shape="circle"
            onClick={() => {
              exportChartAsPNG(ta, alertContext, layoutRef.current, "RadarChart", params.imageDownload);
            }}
          ></Button>
        ) : undefined}
      </Flex>
    );
  };

  if (params.asChild ?? false) {
    return (
      <>
        {/* {params.radarProp?.frontInput ?? false ? <GetRadarInputs {...params} asChild /> : ""} */}
        {(params.labelUse ?? true) && (DataLabel.display ?? true) ? <GetDataLabel {...params} /> : ""}
        <Flex ref={layoutRef} className={cn("data-chart", "RadarChart", params.radarProp?.className ?? "flex-col h-[30rem] w-full")}>
          {GetButtons()}
          <DataRadarItem
            {...radarProp}
            name={params.name}
            noItem={noItem}
            data={data}
            keyLabels={indexInfo.keyLabels}
            form={params.form}
            formFunction={params.formFunction}
          />
        </Flex>
        {/* {!(params.radarProp?.frontInput ?? false) ? <GetRadarInputs {...params} asChild /> : ""} */}
      </>
    );
  } else {
    const inputChild = params.radarProp?.commonInputProp?.asChild ?? false;
    const useLabel = (params.labelUse ?? true) && (DataLabel.display ?? true);
    const mergeValueStyle = useLabel ? params.valueStyle : { ...params.valueStyle, gridColumn: "1 / span 2" };
    return (
      <>
        {/* {(params.radarProp?.frontInput ?? false) && !inputChild ? <GetRadarInputs {...params} asChild={false} /> : ""} */}
        <DataList.Item
          className={cn(params.itemClassName, params.nextItemHidden ? "next-item-hidden" : "", (params.show ?? true) ? "" : "hidden")}
        >
          {params.loading ? (
            <AntSkeleton.Input active={true} block={true} />
          ) : useLabel ? (
            <DataList.Label className={params.labelClassName} style={params.labelStyle}>
              <GetDataLabel {...params} />
            </DataList.Label>
          ) : (
            ""
          )}
          <DataList.Value className={params.valueClassName} style={params.loading ? {} : mergeValueStyle}>
            {params.loading ? (
              <AntSkeleton.Input active={true} block={true} />
            ) : (
              <>
                {/* {(params.radarProp?.frontInput ?? false) && inputChild ? <GetRadarInputs {...params} asChild={true} /> : ""} */}
                {prev}
                <Flex
                  ref={layoutRef}
                  className={cn("data-chart", "RadarChart", params.radarProp?.className ?? "flex-col h-[30rem] w-full")}
                >
                  {GetButtons()}
                  <DataRadarItem
                    {...radarProp}
                    name={params.name}
                    noItem={noItem}
                    data={data}
                    keyLabels={indexInfo.keyLabels}
                    form={params.form}
                    formFunction={params.formFunction}
                  />
                </Flex>
                {next}
                {/* {!(params.radarProp?.frontInput ?? false) && inputChild ? <GetRadarInputs {...params} asChild={true} /> : ""} */}
              </>
            )}
          </DataList.Value>
        </DataList.Item>
        {/* {!(params.radarProp?.frontInput ?? false) && !inputChild ? <GetRadarInputs {...params} asChild={false} /> : ""} */}
      </>
    );
  }
}
